"use client";

import { StoredArticleSummary } from "@/lib/wikipedia";
import { BookIcon, DocumentIcon, CalendarIcon, StopwatchIcon } from "@/components/Icons";

interface HistoryListProps {
  articles: StoredArticleSummary[];
  onSelectArticle: (article: StoredArticleSummary) => void;
}

/**
 * Format seconds into human-readable time string
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

/**
 * Format ISO date string to readable format
 */
function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function HistoryList({ articles, onSelectArticle }: HistoryListProps) {
  // Sort by readAt in reverse chronological order (most recent first)
  const sortedArticles = [...articles].sort(
    (a, b) => new Date(b.readAt).getTime() - new Date(a.readAt).getTime()
  );

  if (sortedArticles.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-500 py-12">
        <BookIcon size={64} className="mb-4 text-gray-400" />
        <p className="text-lg font-medium">No reading history yet</p>
        <p className="text-sm mt-2">Start reading articles to build your history!</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto space-y-2">
      {sortedArticles.map((article, index) => (
        <button
          key={`${article.pageId}-${article.readAt}-${index}`}
          onClick={() => onSelectArticle(article)}
          className="w-full text-left p-3 sm:p-4 bg-white rounded-xl border border-gray-200 hover:border-[#141beb] hover:shadow-md transition-all duration-200 group"
        >
          <div className="flex items-start gap-3">
            {/* Thumbnail or placeholder */}
            {article.thumbnail ? (
              <img
                src={article.thumbnail}
                alt=""
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-gradient-to-br from-[#141beb]/10 to-[#02eec4]/10 flex items-center justify-center flex-shrink-0">
                <DocumentIcon size={24} className="text-[#141beb]" />
              </div>
            )}
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-800 group-hover:text-[#141beb] transition-colors truncate text-sm sm:text-base">
                {article.title}
              </h3>
              
              <div className="flex flex-wrap items-center gap-2 mt-1 text-xs sm:text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <CalendarIcon size={14} />
                  {formatDate(article.readAt)}
                </span>
                <span className="text-gray-300">•</span>
                <span className="flex items-center gap-1">
                  <StopwatchIcon size={14} />
                  {formatDuration(article.timeSpentSeconds)}
                </span>
              </div>
              
              {/* Categories preview */}
              {article.categories.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {article.categories.slice(0, 2).map((cat, catIndex) => (
                    <span
                      key={catIndex}
                      className="px-2 py-0.5 text-xs bg-[#141beb]/5 text-[#141beb] rounded-full truncate max-w-[120px]"
                    >
                      {cat.replace("Category:", "")}
                    </span>
                  ))}
                  {article.categories.length > 2 && (
                    <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">
                      +{article.categories.length - 2}
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Arrow indicator */}
            <span className="text-gray-300 group-hover:text-[#141beb] transition-colors flex-shrink-0">
              →
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}
